import React from 'react'
import {
  CircularProgress,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
} from '@mui/material'
import {
  NavigateBefore,
  NavigateNext,
  ExpandMore,
  KeyboardArrowDown,
} from '@mui/icons-material'
import CustomDatePicker from 'components/molecules/DatePicker/DatePicker'
import { useListOfSurgeries } from './ListOfSugeries.logic'
import { ListOfSurgeriesProps } from './ListOfSurgeries.types'
import './ListOfSurgeries.scss'
import SurgeryBox from 'components/molecules/SurgeryBox/SurgeryBox'
import { useFilters } from 'lib/hooks/useFilters'
import { useSurgeryGrouping } from 'components/ProcedureList/hooks/useSurgeryGrouping'

// Subcomponent for each hospital group
const HospitalAccordionSection = ({
  hospitalName,
  surgeries,
  expanded = false,
  onToggle,
  ...props
}: {
  hospitalName: string
  surgeries: any[]
  expanded?: boolean
  onToggle: (event: React.SyntheticEvent, expanded: boolean) => void
  trayData: any
  isSurgerySelected: (id: string, trayData: any) => boolean
  handleSurgeryChange: (surgery: any) => void
  caseInformation: any
  isNurse: boolean
}) => (
  <Accordion
    expanded={expanded}
    onChange={onToggle}
    className="hospital-accordion"
  >
    <AccordionSummary
      expandIcon={<ExpandMore />}
      className="hospital-accordion-summary"
    >
      <div className="hospital-header">
        <div className="hospital-icon-container">
          <Typography color="var(--primary)" variant="h2">
            {hospitalName.charAt(0)}
          </Typography>
        </div>
        <div className="hospital-info">
          <Typography variant="h6" className="hospital-name">
            {hospitalName} Hospital
          </Typography>
          <Typography variant="body2" className="surgery-count">
            {surgeries.length} {surgeries.length === 1 ? 'case' : 'cases'}
          </Typography>
        </div>
        <Badge
          badgeContent={surgeries.length}
          color="primary"
          className="hospital-badge"
        />
      </div>
    </AccordionSummary>
    <AccordionDetails className="hospital-accordion-details">
      <div className="surgeries-container">
        {surgeries.map((surgery) => (
          <SurgeryBox
            key={surgery._id}
            surgery={surgery}
            caseInformation={props.caseInformation}
            onClick={() => props.handleSurgeryChange(surgery)}
            isNurse={props.isNurse}
            isSelected={props.isSurgerySelected(surgery._id, props.trayData)}
          />
        ))}
      </div>
    </AccordionDetails>
  </Accordion>
)

export const ListOfSurgeries = ({
  traySurgeryId,
  handleSurgeryChange,
  trayData,
}: ListOfSurgeriesProps) => {
  const {
    loading,
    sortedSurgeries,
    caseInformation,
    isNurse,
    handleBackDay,
    handleForwardDay,
    afterDate,
    isSurgerySelected,
    containerRef,
    scrolledToBottom,
    expanded,
    handleAccordionToggle,
  } = useListOfSurgeries({ traySurgeryId })

  const [filters] = useFilters()
  const { groupedSurgeries, hospitalNames } = useSurgeryGrouping({
    surgeries: sortedSurgeries || [],
    filters,
  })

  return (
    <div
      ref={containerRef}
      className={`list-of-surgeries_container${
        scrolledToBottom ? ' scrolled-to-bottom' : ''
      }`}
      data-testid="list-of-surgeries_container"
    >
      {loading && <CircularProgress />}

      <div className={`date-picker_container ${loading ? 'hidden' : ''}`}>
        <IconButton
          data-testid="list-of-surgeries_back-button"
          onClick={handleBackDay}
          size="small"
        >
          <NavigateBefore fontSize="large" />
        </IconButton>

        <CustomDatePicker
          label="Procedure List Date"
          date={afterDate}
          renderDatePicker={false}
        />

        <IconButton
          data-testid="list-of-surgeries_forward-button"
          onClick={handleForwardDay}
          size="small"
        >
          <NavigateNext fontSize="large" />
        </IconButton>
      </div>

      {hospitalNames.length === 0 && !loading && (
        <Typography variant="body2">
          No surgeries available for this date
        </Typography>
      )}

      <div style={{ width: '100%', marginTop: 8 }}>
        {hospitalNames.map((hospitalName) => (
          <HospitalAccordionSection
            key={hospitalName}
            hospitalName={hospitalName}
            surgeries={groupedSurgeries.get(hospitalName) || []}
            expanded={Boolean(expanded === hospitalName)}
            onToggle={handleAccordionToggle(hospitalName)}
            trayData={trayData}
            isSurgerySelected={isSurgerySelected}
            handleSurgeryChange={handleSurgeryChange}
            caseInformation={caseInformation}
            isNurse={!!isNurse}
          />
        ))}
      </div>

      {/* Animated scroll arrow (only show if not at bottom and there is overflow) */}
      {!scrolledToBottom && hospitalNames.length > 0 && (
        <div className="scroll-arrow">
          <KeyboardArrowDown fontSize="inherit" />
        </div>
      )}
    </div>
  )
}
